/* Cart continuity shared by the storefront and the regression tests. */
(() => {
  'use strict';
  const standardVariants = new Set(['47902621401142','47902621433910','47902621466678','47902621499446','47902621532214']);
  const STANDARD_HANDLE = 'receituario-controle-especial-veterinario-3-vias';
  // Propriedades próprias da linha de logotipo exclusivo: nunca removidas pela sincronização do Design.
  const LOGO_PROPERTIES = ['Nome no logotipo','Subtítulo','_pg_kind'];
  // Tipo de linha, espelho de snippets/pg-line-kind.liquid e operations/lib/line-kind.mjs.
  // custom → exige Design. standard → 3 vias. logo → logotipo exclusivo (sem Design).
  function lineKind(item) {
    if(!item) return 'custom';
    if(item.properties?._pg_kind === 'logo' || item.product_type === 'Logotipo' || item.kind === 'logo') return 'logo';
    if(item.handle === STANDARD_HANDLE || standardVariants.has(String(item.variant_id || item.id)) || item.kind === 'standard') return 'standard';
    if(item.requires_shipping === false && !item.gift_card) return 'logo';
    return 'custom';
  }
  function requiresDesign(item) { return lineKind(item) === 'custom'; }
  function logoLines(cart) { return (cart?.items || []).filter(item => lineKind(item) === 'logo'); }
  const logoQueues = new WeakMap();
  const logoVariant = item => String(item.variant_id || item.id);
  function logoPersonalization(original = {}) {
    const clean = (value, limit) => String(value || '').normalize('NFC').replace(/\s+/g, ' ').trim().slice(0, limit);
    return {'Nome no logotipo':clean(original['Nome no logotipo'], 32), 'Subtítulo':clean(original['Subtítulo'], 60)};
  }
  const sameLogoText = (left, right) => JSON.stringify(logoPersonalization(left)) === JSON.stringify(logoPersonalization(right));
  // Shopify can omit empty-valued properties on readback (notably an optional subtitle).
  const propertySignature = original => JSON.stringify(Object.entries(original || {}).filter(([, value]) => value != null && value !== '').sort(([a], [b]) => a.localeCompare(b)));
  function logoGroups(cart, variantId) {
    const groups = new Map();
    for(const line of logoLines(cart)) {
      const id = logoVariant(line);
      if(variantId != null && id !== String(variantId)) continue;
      if(!groups.has(id)) groups.set(id, []);
      groups.get(id).push(line);
    }
    return groups;
  }
  function logoConflicts(cart) {
    return [...logoGroups(cart)].filter(([, lines]) => lines.some(line => !sameLogoText(line.properties, lines[0].properties)))
      .map(([variantId, lines]) => ({variantId, lines}));
  }
  function assertLogos(cart) {
    if([...logoGroups(cart)].some(([, lines]) => lines.length !== 1 || Number(lines[0].quantity) !== 1)) {
      const failure = new Error('Confira os logotipos no carrinho antes de finalizar. Cada logotipo deve aparecer uma única vez.');
      failure.code = 'logo_duplicates';
      failure.conflicts = logoConflicts(cart);
      throw failure;
    }
    return cart;
  }
  function queuedLogoWork(cartAPI, work) {
    const next = (logoQueues.get(cartAPI) || Promise.resolve()).catch(() => {}).then(work);
    logoQueues.set(cartAPI, next);
    return next;
  }
  async function reconcileLogoCart(cartAPI, {variantId, keepKey} = {}, initialCart) {
    let cart = initialCart || await cartAPI(''), repaired = false;
    if(keepKey && !logoLines(cart).some(line => line.key === keepKey && (variantId == null || logoVariant(line) === String(variantId)))) {
      throw new Error('O carrinho mudou. Confira o logotipo antes de continuar.');
    }
    // Snapshot the chosen identity, but always obtain fresh Shopify keys after each write.
    for(const [id, initialLines] of logoGroups(cart, variantId)) {
      const chosen = initialLines.find(line => line.key === keepKey);
      if(initialLines.some(line => !sameLogoText(line.properties, initialLines[0].properties)) && !chosen) continue;
      const keeper = chosen || initialLines[0];
      const merged = Object.assign({}, ...initialLines.filter(line => line !== keeper).map(line => line.properties || {}), keeper.properties || {});
      if(initialLines.length > 1) Object.assign(merged, logoPersonalization(keeper.properties));
      const signature = propertySignature(merged);
      let current = keeper;
      // Save donor metadata before removing duplicate lines, including when a later write fails.
      if(propertySignature(current.properties) !== signature) {
        await cartAPI('/change', {id:current.key, quantity:1, properties:merged});
        repaired = true;
        cart = await cartAPI('');
        current = (logoGroups(cart, id).get(id) || []).find(line => propertySignature(line.properties) === signature);
        if(!current) throw new Error('Não foi possível conferir o logotipo no carrinho. Tente de novo.');
      }
      let lines = logoGroups(cart, id).get(id) || [];
      while(lines.length > 1) {
        current = lines.find(line => line.key === current.key) || lines.find(line => propertySignature(line.properties) === signature);
        if(!current) throw new Error('O carrinho mudou. Confira o logotipo antes de continuar.');
        const remove = lines.find(line => line !== current);
        const previousCount = lines.length;
        await cartAPI('/change', {id:remove.key, quantity:0});
        repaired = true;
        cart = await cartAPI('');
        lines = logoGroups(cart, id).get(id) || [];
        if(lines.length >= previousCount) throw new Error('Não foi possível conferir o logotipo no carrinho. Tente de novo.');
      }
      current = lines[0];
      if(!current || !sameLogoText(current.properties, keeper.properties)) throw new Error('O carrinho mudou. Confira o logotipo antes de continuar.');
      if(Number(current.quantity) !== 1 || propertySignature(current.properties) !== propertySignature(merged)) {
        await cartAPI('/change', {id:current.key, quantity:1, properties:merged});
        repaired = true;
        cart = await cartAPI('');
      }
    }
    cart = await cartAPI('');
    const conflicts = logoConflicts(cart).filter(group => variantId == null || group.variantId === String(variantId));
    // Any unresolved quantity/identical duplicate means a write did not take effect.
    const unresolved = new Set(conflicts.map(group => group.variantId));
    for(const [id, lines] of logoGroups(cart, variantId)) {
      if(!unresolved.has(id) && (lines.length !== 1 || Number(lines[0].quantity) !== 1)) throw new Error('Não foi possível conferir o logotipo no carrinho. Tente de novo.');
    }
    return {cart, conflicts, repaired};
  }
  function reconcileLogos(cartAPI, options = {}) {
    return queuedLogoWork(cartAPI, () => reconcileLogoCart(cartAPI, options));
  }
  function ensureLogo(cartAPI, {variantId, properties:proposed = {}, update = false, keepKey} = {}) {
    return queuedLogoWork(cartAPI, async () => {
      const id = String(variantId || '');
      if(!/^\d+$/.test(id)) throw new Error('Logotipo inválido.');
      const wanted = {...proposed, ...logoPersonalization(proposed), _pg_kind:'logo'};
      let result = await reconcileLogoCart(cartAPI, {variantId:id, keepKey});
      let line = logoLines(result.cart).find(item => logoVariant(item) === id);
      const conflict = () => ({...result, status:'conflict', line, conflicts:result.conflicts.length ? result.conflicts : [{variantId:id, lines:line ? [line] : []}]});
      if(result.conflicts.length) return conflict();
      if(line) {
        if(!sameLogoText(line.properties, wanted) && !update) return conflict();
        if(!update || propertySignature(line.properties) === propertySignature({...line.properties, ...wanted})) return {...result, status:'already-present', line};
        await cartAPI('/change', {id:line.key, quantity:1, properties:{...line.properties, ...wanted}});
      } else {
        try {
          await cartAPI('/add', {items:[{id, quantity:1, properties:wanted}]});
        } catch(failure) {
          // A timeout may follow an accepted write. Read once; never repeat the add blindly.
          let observed;
          try { observed = await cartAPI(''); } catch { throw failure; }
          const found = logoLines(observed).find(item => logoVariant(item) === id);
          if(!found) throw failure;
          if(!sameLogoText(found.properties, wanted)) {
            result = {cart:observed, conflicts:logoConflicts(observed), repaired:result.repaired};
            line = found;
            return conflict();
          }
        }
      }
      const status = line ? 'updated' : 'added';
      const verified = await reconcileLogoCart(cartAPI, {variantId:id});
      result = {...verified, repaired:result.repaired || verified.repaired};
      line = logoLines(result.cart).find(item => logoVariant(item) === id);
      if(result.conflicts.length || (line && !sameLogoText(line.properties, wanted))) return conflict();
      if(!line) throw new Error('Não foi possível conferir o logotipo no carrinho. Tente de novo.');
      return {...result, status, line};
    });
  }
  function properties(original, profile, templates) {
    const result = {...original};
    delete result['Modelo Petgraf'];
    delete result['Personalização'];
    // pg-design.js entrega o Design salvo em profile.saved; aceita também o formato plano.
    const design = profile.saved || profile;
    const fields = design.fields || {};
    const text = String(fields.observations || '').trim();
    const named = ['clinic','professional','crmv','crmvState','specialty']
      .map(key => String(fields[key] || '').trim()).filter(Boolean).join(' \u00b7 ');
    const art = design.artwork && design.artwork.kind === 'template'
      ? 'Modelo Petgraf' + (templates && templates[design.artwork.templateId] ? ': ' + templates[design.artwork.templateId].name : '')
      : design.artwork && design.artwork.kind === 'exclusive'
      ? 'Logotipo exclusivo Petgraf: ' + (design.artwork.title || '') + (design.artwork.name ? ' (' + design.artwork.name + ')' : '')
      : design.artwork ? 'Logotipo enviado pelo cliente' : '';
    const visible = {};
    if(art) visible['Arte'] = art;
    if(named) visible['Identifica\u00e7\u00e3o'] = named.slice(0, 500);
    if(text) visible['Dados para a arte'] = text.slice(0, 900);
    return {...result,
      Design: 'Salvo',
      ...visible,
      _pg_design_id: profile.id,
      _pg_design_revision: String(profile.revision)
    };
  }
  function clearProperties(original) {
    const result = {...original};
    delete result['Personalização']; delete result['Modelo Petgraf'];
    delete result.Design; delete result._pg_design_id; delete result._pg_design_revision;
    delete result['Arte']; delete result['Identifica\u00e7\u00e3o']; delete result['Dados para a arte'];
    return result;
  }
  function desiredProperties(item, profile, templates) { return requiresDesign(item) ? properties(item.properties, profile, templates) : clearProperties(item.properties); }
  function matches(item, profile) {
    if(!requiresDesign(item)) return JSON.stringify(item.properties || {}) === JSON.stringify(clearProperties(item.properties));
    return item.properties?._pg_design_id === profile.id && Number(item.properties?._pg_design_revision) === profile.revision;
  }
  async function sync(cartAPI, profile, templates) {
    let cart = await cartAPI('');
    const maximumChanges = cart.items.length + 1;
    // Changing properties changes keys and can merge two otherwise identical lines.
    // Select from the returned cart each time, retaining its current quantities.
    for(let attempt = 0; attempt < maximumChanges; attempt++) {
      const item = cart.items.find(item => !matches(item, profile) || JSON.stringify(item.properties) !== JSON.stringify(desiredProperties(item, profile, templates)));
      if(!item) break;
      cart = await cartAPI('/change', {id:item.key, quantity:item.quantity, properties:desiredProperties(item, profile, templates)});
    }
    const verified = await cartAPI('');
    if(verified.items.some(item => !matches(item, profile))) throw new Error('Alguns itens ainda não receberam o Design. Tente de novo.');
    return verified;
  }
  function orderItems(cart) {
    return cart.items.filter(requiresDesign).map(item => ({variantId:String(item.variant_id), quantity:item.quantity, note:item.properties?.['Observação deste material'] || '', kit:item.properties?._pg_kit || ''}))
      .sort((a,b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  }
  window.petgrafDesignCart = Object.freeze({properties, clearProperties, sync, orderItems, requiresDesign, lineKind, logoLines, LOGO_PROPERTIES, logoPersonalization, logoConflicts, assertLogos, reconcileLogos, ensureLogo});
})();
